import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DistributorTopFilter from "./DistributorConfigFilter";
import { Box, Chip } from "@mui/material";
import {
  DistributorFeature,
  fetchDistributorMeta,
  filterDistributorsBySelections,
} from "../../../../../services/distributorMetaService";
import {
  fetchChildrenUsers,
  fetchGeographicalLevels,
  fetchLocationUsers,
  fetchUsersByDesignation,
} from "../../../../../services/reportsDataService";
import { DrillDownPathItem, PathSummaryItem, summarizeDrillDownPath } from "../../../../newMdmReports/utils/hierarchyHelpers";
import { useFullHierarchyLoaders } from "./useFullHerirarchy";
import { ConfirmationPopUp } from "../../../../../components/popup/confirmationPopUp";

export interface DistributorFilter {
  enabled: boolean;
  field: string;
  label: string;
}
export interface SalesHierarchyFilter {
  enabled: boolean;
  hierarchyMap: Record<string, string[]>;
  hierarchyOrder: string[];
  levelFilterField: string;
  levelFilterLabel: string;
  valueFilterField: string;
  valueFilterLabel: string;
}

export interface GeographicalHierarchyFilter {
  levelFilterField: string;
  levelFilterLabel: string;
  valueFilterField: string;
  valueFilterLabel: string;
  enabled: boolean;
}
interface Props{
    name:string,
    values:any,
 setValues: React.Dispatch<React.SetStateAction<any>>;
}

const FullHerirarchy = (props:Props) => {
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [distributorFeatures, setDistributorFeatures] = useState<
    DistributorFeature[]
  >([]);
  const [geoHierarchyOrder, setGeoHierarchyOrder] = useState<string[]>([]);
  const [salesDrillDownPath, setSalesDrillDownPath] = useState<
    DrillDownPathItem[]
  >([]);
  const [salesOptionsCache, setSalesOptionsCache] = useState<
    Record<string, { label: string; value: string }[]>
  >({});
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [searchTextMap, setSearchTextMap] = useState<Record<string, string>>(
    {}
  );
  const [isHierarchySyncing, setIsHierarchySyncing] = useState(false);
   const pendingSourceChange = React.useRef<DistributorSource | null>(null);

  const [geoOptionsCache, setGeoOptionsCache] = useState<
    Record<string, { label: string; value: string }[]>
  >({});
  const distributorFilter = useRef< DistributorFilter >({label:"Select Distributor",enabled:true,field:"distributor_code"});
  const salesHierarchyFilter = useRef<SalesHierarchyFilter>({enabled:true,levelFilterField:"salesLevel",levelFilterLabel:"Select Level",valueFilterField:"salesValue",valueFilterLabel:"Select Value",hierarchyMap:{},hierarchyOrder:[
          "saleshead",
          "nsm",
          "rsm",
          "zm",
          "asm",
          "tsm",
          "tgo",
          "sde",
          "supplier"
        ],});
    const [showSourceChangeConfirm, setShowSourceChangeConfirm] = useState(false);
      const distributorFieldKey = distributorFilter?.current?.field;

  const geographicalHierarchyFilter=useRef<GeographicalHierarchyFilter >({enabled:true,levelFilterField:"geographicalLevel",levelFilterLabel:"Select Geographical Level",valueFilterField:"geographicalValue",valueFilterLabel:"Select Geographical Value"});
  const salesApplyJustClicked = React.useRef(false);
  const geoApplyJustClicked = React.useRef(false);


  const [geoDrillDownPath, setGeoDrillDownPath] = useState<DrillDownPathItem[]>(
    []
  );
      const geoDrillDownPathKey = useMemo(() => JSON.stringify(geoDrillDownPath), [geoDrillDownPath]);
    const salesDrillDownPathKey = useMemo(() => JSON.stringify(salesDrillDownPath), [salesDrillDownPath]);
    
    const distributorTypeKey = useMemo(() => JSON.stringify(filters['distributor_type'] || []), [filters['distributor_type']]);
    const distributorDivisionKey = useMemo(() => JSON.stringify(filters['distributor_division'] || []), [filters['distributor_division']]);
  const [optionsMap, setOptionsMap] = useState<
    Record<string, { label: string; value: string }[]>
  >({});
  const [distributorMetaOptionCache, setDistributorMetaOptionCache] = useState<{
    types: { label: string; value: string }[];
    divisions: { label: string; value: string }[];
  }>({ types: [], divisions: [] });
  const getValuesFromPath = (
    path: DrillDownPathItem[],
    level: string
  ): string[] => {
    if (!path || path.length === 0) {
      return [];
    }
    const values = path
      .filter((item) => item.level === level)
      .map((item) => item.value);
    return values;
  };
  const {
    loadSalesLevels,
    loadSalesValues,
    loadGeographicalLevels,
    loadGeographicalValues,
  } = useFullHierarchyLoaders({
    salesHierarchyFilter: salesHierarchyFilter.current,
    geographicalHierarchyFilter: geographicalHierarchyFilter.current,
    filters,
    setOptionsMap,
    setLoadingMap,
    salesDrillDownPath,
    geoDrillDownPath,
    geoHierarchyOrder,
    setGeoHierarchyOrder,
    salesOptionsCache,
    setSalesOptionsCache,
    geoOptionsCache,
    setGeoOptionsCache,
  });
  type DistributorSource = "geographical" | "sales";

  const resolveDefaultDistributorSource = (): DistributorSource => {
    if (geographicalHierarchyFilter?.current?.enabled) {
      return "geographical";
    }
    if (salesHierarchyFilter?.current?.enabled) {
      return "sales";
    }
    return "geographical";
  };
  const [distributorSource, setDistributorSource] = useState<DistributorSource>(
    resolveDefaultDistributorSource()
  );
const hasSalesSelections = useCallback(() => {
    if (salesDrillDownPath.length > 0) return true;
    if (!salesHierarchyFilter) return false;
    const levelKey = salesHierarchyFilter.current?.levelFilterField;
    const hierarchyOrder = salesHierarchyFilter?.current?.hierarchyOrder || [];
   
    // if (filters[levelKey] && filters[levelKey].length > 0) return true;
    return hierarchyOrder.some(level => filters[level] && filters[level].length > 0);
  }, [salesDrillDownPath, filters,salesHierarchyFilter]);
 const handleFilterInputChange = (key: string, inputValue: string) => {
    // Update the search text immediately for UI responsiveness
    setSearchTextMap(prev => ({ ...prev, [key]: inputValue }));
    
    // Don't call loadFilterOptions for distributor filter - it uses loadDistributorOptions instead
    // The search filtering for distributors is handled client-side in CompactCheckboxDropdown
    const distributorFieldKey = distributorFilter?.current?.field;
    if (key === distributorFieldKey) {
      return;
    }
    
    // Clear any existing timeout for this filter
   
  };
  const hasGeoSelections = useCallback(() => {
    if (geoDrillDownPath.length > 0) return true;
    if (!geographicalHierarchyFilter) return false;
    const levelKey = geographicalHierarchyFilter?.current?.levelFilterField;
    // if (filters[levelKey] && filters[levelKey].length > 0) return true;
    return geoHierarchyOrder.some(level => filters[level] && filters[level].length > 0);
  }, [geoDrillDownPath, filters, geographicalHierarchyFilter, geoHierarchyOrder]);

  const buildHierarchyPathUpToLevel = (
    existingPath: DrillDownPathItem[],
    filters: Record<string, string[]>,
    hierarchyOrder: string[] | undefined,
    uptoLevel?: string | null
  ): DrillDownPathItem[] => {
    if (!uptoLevel) {
      return existingPath;
    }

    let orderedLevels: string[] = [];
    if (hierarchyOrder && hierarchyOrder.length > 0) {
      orderedLevels = [...hierarchyOrder];
    }

    if (!orderedLevels.includes(uptoLevel)) {
      orderedLevels.push(uptoLevel);
    }

    const newPath: DrillDownPathItem[] = [];

    for (const level of orderedLevels) {
      // Check if level exists in filters (even if empty array)
      // If level is in filters, use it (respects intentional clearing)
      // If level is not in filters at all, fall back to existing path
      const pendingValues =
        level in filters
          ? filters[level]
          : getValuesFromPath(existingPath, level);

      if (pendingValues && pendingValues.length > 0) {
        pendingValues.forEach((value) => newPath.push({ level, value }));
      }

      if (level === uptoLevel) {
        break;
      }
    }

    return newPath;
  };

    useEffect(() => {
      if (salesHierarchyFilter?.current?.enabled) {
        loadSalesLevels();
      }
    }, [salesHierarchyFilter?.current?.enabled, loadSalesLevels]);
  
    useEffect(() => {
      if (geographicalHierarchyFilter?.current?.enabled) {
        loadGeographicalLevels();
      }
    }, [geographicalHierarchyFilter?.current?.enabled, loadGeographicalLevels]);
     const handleGeoNextLevel = () => {
    if (!geographicalHierarchyFilter) return;
    
    
    const levelKey = geographicalHierarchyFilter.current?.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    const selectedValues = currentLevel ? filters[currentLevel] || [] : [];
    
    
    if (!currentLevel) return;
    
    // Set syncing state to show loading during Apply
    setIsHierarchySyncing(true);
    geoApplyJustClicked.current = true;
    
    const updatedPath = buildHierarchyPathUpToLevel(
      geoDrillDownPath,
      filters,
      geoHierarchyOrder,
      currentLevel
    );
    setGeoDrillDownPath(updatedPath);
    
    // Clear cache for child levels
    const currentLevelIndex = geoHierarchyOrder.indexOf(currentLevel);
    
    if (currentLevelIndex !== -1) {
      setOptionsMap(prev => {
        const updated = { ...prev };
        // Clear cache for all levels after this one
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete updated[geoHierarchyOrder[i]];
        }
        // Clear distributor cache
        if (distributorFilter?.current?.enabled) {
          delete updated[distributorFilter?.current?.field];
        }
       
        return updated;
      });

      setGeoOptionsCache(prev => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete updated[geoHierarchyOrder[i]];
        }
        return updated;
      });
      
      // Clear child levels from filters as well
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        delete newFilters[currentLevel];
        // Clear all child levels
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete newFilters[geoHierarchyOrder[i]];
        }
        
        // Clear custom filter selections when geo hierarchy changes (affects distributors)
     
        
        return newFilters;
      });
      
      // Clear search text for custom filters when geo hierarchy changes
      setSearchTextMap(prev => {
        const updated = { ...prev };
      
        return updated;
      });
    } else {
      // Fallback if level not found in hierarchy
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        delete newFilters[currentLevel];
        return newFilters;
      });
    }
    
    loadGeographicalLevels();
  };

  const handleSalesLevelChange = (value: string | null) => {
    if (!salesHierarchyFilter) return;

    const levelKey = salesHierarchyFilter?.current?.levelFilterField;
    const prevLevel = filters[levelKey]?.[0];
    const hierarchyOrder = salesHierarchyFilter?.current?.hierarchyOrder || [];
    setFilters((prev) => {
      const newFilters = { ...prev };

      // Just update the level selection, don't clear values
      if (value) {
        newFilters[levelKey] = [value];

        // Determine if parent context validation is needed
        const valueIndex = hierarchyOrder.indexOf(value);

        // Check if user is navigating UP the hierarchy (going to a parent level)
        // by checking if there are selections in levels BELOW the target level
        // OR if the previous level was a child level
        let isNavigatingUp = false;
        if (valueIndex !== -1) {
          // Check if previous level was a child level
          if (prevLevel) {
            const prevLevelIndex = hierarchyOrder.indexOf(prevLevel);
            if (prevLevelIndex !== -1 && prevLevelIndex > valueIndex) {
              isNavigatingUp = true;
            }
          }

          // Also check if any child levels have values
          if (!isNavigatingUp) {
            for (let i = valueIndex + 1; i < hierarchyOrder.length; i++) {
              const childLevel = hierarchyOrder[i];
              if (prev[childLevel] && prev[childLevel].length > 0) {
                isNavigatingUp = true;
                break;
              }
            }
          }
        }

        // Validate parent context: allow if first level, has parent in path/filters, OR navigating up
        let hasValidParentContext = valueIndex === 0 || isNavigatingUp;

        if (!hasValidParentContext && valueIndex > 0) {
          // Check for parent context in drillDownPath (committed selections)
          if (salesDrillDownPath.length > 0) {
            hasValidParentContext = salesDrillDownPath.some((item) => {
              const itemIndex = hierarchyOrder.indexOf(item.level);
              return itemIndex !== -1 && itemIndex < valueIndex;
            });
          }

          // Also check for parent context in filters (building phase)
          if (!hasValidParentContext) {
            for (let i = 0; i < valueIndex; i++) {
              const parentLevel = hierarchyOrder[i];
              if (prev[parentLevel] && prev[parentLevel].length > 0) {
                hasValidParentContext = true;
                break;
              }
            }
          }
        }

        // Restoration logic:
        // - If filter values exist (active editing session) → preserve them
        // - Otherwise, restore from drillDownPath (first open or after cleanup)
        const committedValues = getValuesFromPath(salesDrillDownPath, value);
        const currentValues = prev[value] || [];

        if (currentValues.length > 0) {
          // Active editing session - preserve current values
          newFilters[value] = [...currentValues];
        } else if (committedValues.length > 0) {
          // First open - restore from drillDownPath
          newFilters[value] = [...committedValues];
        } else if (!hasValidParentContext) {
          // Clear if no values and no valid parent context
          if (newFilters[value]) {
            delete newFilters[value];
          }
        }
        // If no values but valid parent, leave empty (ready for new selection)
      } else {
        delete newFilters[levelKey];
      }
      return newFilters;
    });

    // Only load values if:
    // 1. Value is not empty
    // 2. Options don't exist in cache OR switching to a different level
    // Note: if switching levels but cache exists, loadSalesValues will use cache
    const hasCache = value
      ? salesOptionsCache[value] && salesOptionsCache[value].length > 0
      : false;
    const hasOptionsInMap = value
      ? optionsMap[value] && optionsMap[value].length > 0
      : false;
    const shouldLoad =
      value && (!hasOptionsInMap || (prevLevel !== value && !hasCache));

    if (shouldLoad) {
      loadSalesValues(value);
    } else if (value && hasCache && !hasOptionsInMap) {
      // Restore from cache without API call
      setOptionsMap((prev) => ({ ...prev, [value]: salesOptionsCache[value] }));
    }

    // Clear distributor search text when sales hierarchy level changes
    if (distributorFilter?.current?.enabled && distributorFilter?.current?.field) {
      setSearchTextMap((prev) => {
        const updated = { ...prev };
        delete updated[distributorFilter!.current?.field];
        return updated;
      });
    }
  };

  const handleSalesValueChange = (values: string[]) => {
    if (!salesHierarchyFilter) return;

    const levelKey = salesHierarchyFilter.current?.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];

    if (!currentLevel) return;

    // When parent selections change, clear cache for all child levels
    const hierarchyOrder = salesHierarchyFilter.current?.hierarchyOrder || [];
    const currentLevelIndex = hierarchyOrder.indexOf(currentLevel);

    if (currentLevelIndex !== -1) {
      setOptionsMap((prev) => {
        const updated = { ...prev };
        // Clear cache for all levels after this one
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete updated[hierarchyOrder[i]];
        }
        return updated;
      });

      setSalesOptionsCache((prev) => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete updated[hierarchyOrder[i]];
        }
        return updated;
      });
    }

    // Update filter values - DON'T clear child values (preserve during editing)
    setFilters((prev) => ({
      ...prev,
      [currentLevel]: values,
    }));

    // DON'T update drillDownPath here - only update on Apply button!
  };
  const handleSalesDropdownClose = useCallback(() => {
    if (!salesHierarchyFilter) return;
    
    // If Apply was just clicked, skip cleanup and just reset the flag
    if (salesApplyJustClicked.current) {
      salesApplyJustClicked.current = false;
      setIsHierarchySyncing(false);
      return;
    }
    
    
    const hierarchyOrder =salesHierarchyFilter?.current?.hierarchyOrder || [];
    const levelKey = salesHierarchyFilter?.current?.levelFilterField;
    
    setFilters(prev => {
      const newFilters = { ...prev };
      let hasChanges = false;
      
      // For each level in hierarchy, check if values exist but are not committed
      hierarchyOrder.forEach(level => {
        const filterValues = prev[level] || [];
        const committedValues = getValuesFromPath(salesDrillDownPath, level);
        
        // If there are values in filters but not in drillDownPath, clear them
        if (filterValues.length > 0 && committedValues.length === 0) {
          delete newFilters[level];
          hasChanges = true;
        }
        // If values differ from committed, revert to committed
        else if (filterValues.length > 0 && JSON.stringify(filterValues.sort()) !== JSON.stringify(committedValues.sort())) {
          if (committedValues.length > 0) {
            newFilters[level] = [...committedValues];
          } else {
            delete newFilters[level];
          }
          hasChanges = true;
        }
      });
      
      // Clear level selection if no committed path exists
      if (salesDrillDownPath.length === 0 && newFilters[levelKey]) {
        delete newFilters[levelKey];
        hasChanges = true;
      }
      
      return hasChanges ? newFilters : prev;
    });
    
    // Clear options cache for uncommitted levels
    setOptionsMap(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      
      hierarchyOrder.forEach(level => {
        const committedValues = getValuesFromPath(salesDrillDownPath, level);
        if (committedValues.length === 0 && updated[level]) {
          delete updated[level];
          hasChanges = true;
        }
      });
      
      return hasChanges ? updated : prev;
    });
    
    setSalesOptionsCache(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      
      hierarchyOrder.forEach(level => {
        const committedValues = getValuesFromPath(salesDrillDownPath, level);
        if (committedValues.length === 0 && updated[level]) {
          delete updated[level];
          hasChanges = true;
        }
      });
      
      return hasChanges ? updated : prev;
    });
    
    // Reset syncing state after cleanup completes
    setIsHierarchySyncing(false);
  }, [salesHierarchyFilter, salesDrillDownPath]);
  const handleSalesNextLevel = () => {
    if (!salesHierarchyFilter) return;

    const levelKey = salesHierarchyFilter.current?.levelFilterField;
    const currentLevel = filters[levelKey]?.[0];
    const selectedValues = currentLevel ? filters[currentLevel] || [] : [];

    if (!currentLevel) return;

    // Set syncing state to show loading during Apply
    setIsHierarchySyncing(true);
    salesApplyJustClicked.current = true;

    const updatedPath = buildHierarchyPathUpToLevel(
      salesDrillDownPath,
      filters,
      salesHierarchyFilter?.current?.hierarchyOrder || [],
      currentLevel
    );
    setSalesDrillDownPath(updatedPath);

    // Clear cache for child levels
    const hierarchyOrder = salesHierarchyFilter.current?.hierarchyOrder || [];
    const currentLevelIndex = hierarchyOrder.indexOf(currentLevel);
    const supplierLevel =
      hierarchyOrder.length > 0
        ? hierarchyOrder[hierarchyOrder.length - 1]
        : "supplier";
    const isSupplierLevelChange = currentLevel === supplierLevel;

    if (currentLevelIndex !== -1) {
      setOptionsMap((prev) => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete updated[hierarchyOrder[i]];
        }

        return updated;
      });

      setSalesOptionsCache((prev) => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete updated[hierarchyOrder[i]];
        }
        return updated;
      });

      // Clear child levels from filters as well
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        delete newFilters[currentLevel];
        // Clear all child levels
        for (let i = currentLevelIndex + 1; i < hierarchyOrder.length; i++) {
          delete newFilters[hierarchyOrder[i]];
        }

        // If supplier level changed, also clear custom filter selections

        return newFilters;
      });

      // Clear search text for custom filters if supplier level changed
      if (isSupplierLevelChange) {
        setSearchTextMap((prev) => {
          const updated = { ...prev };
          return updated;
        });
      }
    } else {
      // Fallback if level not found in hierarchy
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        delete newFilters[currentLevel];
        return newFilters;
      });
    }

    loadSalesLevels();
  };
  const clearDistributorSelections = useCallback(() => {
    if (!distributorFilter?.current?.field) {
      return;
    }
    const distributorFieldKey = distributorFilter.current?.field;
    setFilters((prev) => {
      if (!prev[distributorFieldKey]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[distributorFieldKey];

      return updated;
    });
    setOptionsMap((prev) => {
      if (!prev[distributorFieldKey]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[distributorFieldKey];

      return updated;
    });
    setSearchTextMap((prev) => {
      if (!prev[distributorFieldKey]) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[distributorFieldKey];

      return updated;
    });
  }, [distributorFilter?.current?.field]);

  const handleSalesPathReset = (level: string, startIndex: number) => {
    if (!salesHierarchyFilter) return;

    const levelKey = salesHierarchyFilter.current?.levelFilterField;
    const hierarchyOrder = salesHierarchyFilter.current?.hierarchyOrder || [];

    // Trim path to this level (remove this level and everything after)
    setSalesDrillDownPath((prev) => {
      const newPath = prev.slice(0, startIndex);
      return newPath;
    });

    setFilters((prev) => {
      const newFilters = { ...prev };

      // Clear the level dropdown selection
      delete newFilters[levelKey];

      const supplierLevel =
        hierarchyOrder.length > 0
          ? hierarchyOrder[hierarchyOrder.length - 1]
          : "supplier";
      const isSupplierLevelCleared =
        hierarchyOrder.length > 0
          ? hierarchyOrder.indexOf(level) <=
            hierarchyOrder.indexOf(supplierLevel)
          : false;

      if (hierarchyOrder.length > 0) {
        const levelIndex = hierarchyOrder.indexOf(level);
        const levelsToDelete: string[] = [];
        hierarchyOrder.forEach((hierLevel, idx) => {
          if (levelIndex !== -1 && idx >= levelIndex) {
            delete newFilters[hierLevel];
            levelsToDelete.push(hierLevel);
          }
        });
      } else {
        delete newFilters[level];
      }

      return newFilters;
    });

    // Clear options cache for this level and below
    setOptionsMap((prev) => {
      const updated = { ...prev };
      if (hierarchyOrder.length > 0) {
        const levelIndex = hierarchyOrder.indexOf(level);
        hierarchyOrder.forEach((hierLevel, idx) => {
          if (levelIndex !== -1 && idx >= levelIndex) {
            delete updated[hierLevel];
          }
        });
      } else {
        delete updated[level];
      }
      return updated;
    });

    // Clear sales options cache for this level and below
    setSalesOptionsCache((prev) => {
      const updated = { ...prev };
      if (hierarchyOrder.length > 0) {
        const levelIndex = hierarchyOrder.indexOf(level);
        hierarchyOrder.forEach((hierLevel, idx) => {
          if (levelIndex !== -1 && idx >= levelIndex) {
            delete updated[hierLevel];
          }
        });
      } else {
        delete updated[level];
      }
      return updated;
    });

    // Clear distributor selection if distributor source is sales
    if (distributorSource === "sales" && distributorFilter?.current?.enabled) {
      clearDistributorSelections();
    } else if (distributorFilter?.current?.enabled && distributorFilter?.current?.field) {
      // Clear distributor and custom filters when sales hierarchy values change (even if not the active source)
      const distributorFieldKey = distributorFilter.current?.field;
      setFilters((prev) => {
        const newFilters = { ...prev };
        if (newFilters[distributorFieldKey]) {
          delete newFilters[distributorFieldKey];
        }
        // Also clear custom filter selections

        return newFilters;
      });
      setOptionsMap((prev) => {
        const updated = { ...prev };
        if (updated[distributorFieldKey]) {
          delete updated[distributorFieldKey];
        }
        // Clear custom filter caches

        return updated;
      });
      setSearchTextMap((prev) => {
        const updated = { ...prev };
        delete updated[distributorFieldKey];
        // Clear custom filter search text

        return updated;
      });
    }

    // Reload levels dropdown so user can select a level again
    loadSalesLevels();
  };
   const clearSalesHierarchySelections = useCallback(() => {
      if (!salesHierarchyFilter) return;
      
      const levelKey = salesHierarchyFilter.current?.levelFilterField;
      const hierarchyOrder = salesHierarchyFilter.current?.hierarchyOrder || [];
      
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        hierarchyOrder.forEach(level => {
          delete newFilters[level];
        });
        return newFilters;
      });
      
      setSalesDrillDownPath([]);
      
      setOptionsMap(prev => {
        const updated = { ...prev };
        hierarchyOrder.forEach(level => {
          delete updated[level];
        });
        return updated;
      });
      setSalesOptionsCache({});
    }, [salesHierarchyFilter]);
  
    const clearGeoHierarchySelections = useCallback(() => {
     
      
      const levelKey = geographicalHierarchyFilter?.current?.levelFilterField;
      
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[levelKey];
        geoHierarchyOrder.forEach(level => {
          delete newFilters[level];
        });
        return newFilters;
      });
      
      setGeoDrillDownPath([]);
      
      setOptionsMap(prev => {
        const updated = { ...prev };
        geoHierarchyOrder.forEach(level => {
          delete updated[level];
        });
        return updated;
      });
      setGeoOptionsCache({});
    }, [geographicalHierarchyFilter, geoHierarchyOrder]);

  const loadDistributorMetaOptions = useCallback(async () => {
    setLoadingMap((prev) => ({
      ...prev,
      distributor_type: true,
      distributor_division: true,
    }));
    try {
      const meta = await fetchDistributorMeta();
      const divisionOptions = meta.divisions.map((value) => ({
        label: value,
        value,
      }));
      const typeOptions = meta.types.map((value) => ({ label: value, value }));
      // Store the full features array in memory for filtering
      setDistributorFeatures(meta.features);
      setDistributorMetaOptionCache({
        types: typeOptions,
        divisions: divisionOptions,
      });

      setOptionsMap((prev) => ({
        ...prev,
        distributor_division: divisionOptions,
        distributor_type: typeOptions,
      }));
    } catch (error) {
      setOptionsMap((prev) => ({
        ...prev,
        distributor_division:
          prev.distributor_division || distributorMetaOptionCache.divisions,
        distributor_type:
          prev.distributor_type || distributorMetaOptionCache.types,
      }));
    } finally {
      setLoadingMap((prev) => ({
        ...prev,
        distributor_type: false,
        distributor_division: false,
      }));
    }
  }, []);
   useEffect(() => {
        loadDistributorMetaOptions();
      }, [loadDistributorMetaOptions]);
       useEffect(() => {
          if (distributorFilter?.current?.enabled) {
            const fieldKey = distributorFilter.current?.field;
            setOptionsMap(prev => {
              const updated = { ...prev };
              delete updated[fieldKey];
              return updated;
            });
      
            setFilters(prev => {
              const updated = { ...prev };
              if (updated[fieldKey]) {
                delete updated[fieldKey];
              }
              return updated;
            });
            setSearchTextMap(prev => {
              const updated = { ...prev };
              if (updated[fieldKey]) {
                delete updated[fieldKey];
              }
              return updated;
            });
          }
          
        }, [distributorFilter?.current?.enabled, geoDrillDownPathKey, salesDrillDownPathKey, distributorTypeKey, distributorDivisionKey, distributorSource]);
          const handleSalesResetAll = () => {
    if (!salesHierarchyFilter) return;
    
    const levelKey = salesHierarchyFilter.current?.levelFilterField;
    const hierarchyOrder = salesHierarchyFilter.current?.hierarchyOrder || [];
    
    setFilters(prev => {
      const newFilters = { ...prev };
      // Clear level selection
      delete newFilters[levelKey];
      // Clear all level values
      hierarchyOrder.forEach(level => {
        delete newFilters[level];
      });
      return newFilters;
    });
    
    // Clear drill-down path
    setSalesDrillDownPath([]);
    
    // Clear options cache
    setOptionsMap(prev => {
      const updated = { ...prev };
      hierarchyOrder.forEach(level => {
        delete updated[level];
      });
      return updated;
    });
    setSalesOptionsCache({});

    // Clear distributor selection if distributor source is sales
    if (distributorSource === "sales" && distributorFilter?.current?.enabled) {
      clearDistributorSelections();
    }
  };
   const handleDistributorSourceChange = useCallback((source: DistributorSource) => {
      if (source === distributorSource) {
        return;
      }
      console.log(source)
      // Check if there are selections in the other hierarchy
      const hasOtherSelections = source === "geographical" 
        ? hasSalesSelections() 
        : hasGeoSelections();
      
      if (hasOtherSelections) {
        // Show confirmation dialog
          console.log(source)
        pendingSourceChange.current = source;
        setShowSourceChangeConfirm(true);
      } else {
        // No selections, switch directly
        setDistributorSource(source);
        clearDistributorSelections();
        // Clear distributor search text when switching source
        if (distributorFilter?.current?.field) {
          setSearchTextMap(prev => {
            const updated = { ...prev };
            delete updated[distributorFilter!.current?.field];
            return updated;
          });
        }
      }
    }, [distributorSource, clearDistributorSelections, hasSalesSelections, hasGeoSelections,distributorFilter?.current?.field]);

  const handleGeographicalLevelChange = (value: string | null) => {
    const levelKey = "geographicalLevel";
    const prevLevel = filters[levelKey]?.[0];
    setFilters((prev) => {
      const newFilters = { ...prev };

      // Just update the level selection, don't clear values
      if (value) {
        newFilters[levelKey] = [value];

        const valueIndex = geoHierarchyOrder.indexOf(value);

        // Check if user is navigating UP the hierarchy (going to a parent level)
        // by checking if there are selections in levels BELOW the target level
        // OR if the previous level was a child level
        let isNavigatingUp = false;
        if (valueIndex !== -1) {
          // Check if previous level was a child level
          if (prevLevel) {
            const prevLevelIndex = geoHierarchyOrder.indexOf(prevLevel);
            if (prevLevelIndex !== -1 && prevLevelIndex > valueIndex) {
              isNavigatingUp = true;
            }
          }

          // Also check if any child levels have values
          if (!isNavigatingUp) {
            for (let i = valueIndex + 1; i < geoHierarchyOrder.length; i++) {
              const childLevel = geoHierarchyOrder[i];
              if (prev[childLevel] && prev[childLevel].length > 0) {
                isNavigatingUp = true;
                break;
              }
            }
          }
        }

        // Validate parent context: allow if first level, has parent in path/filters, OR navigating up
        let hasValidParentContext = valueIndex === 0 || isNavigatingUp;

        if (!hasValidParentContext && valueIndex > 0) {
          // Check for parent context in drillDownPath (committed selections)
          if (geoDrillDownPath.length > 0) {
            hasValidParentContext = geoDrillDownPath.some((item) => {
              const itemIndex = geoHierarchyOrder.indexOf(item.level);
              return itemIndex !== -1 && itemIndex < valueIndex;
            });
          }

          // Also check for parent context in filters (building phase)
          if (!hasValidParentContext) {
            for (let i = 0; i < valueIndex; i++) {
              const parentLevel = geoHierarchyOrder[i];
              if (prev[parentLevel] && prev[parentLevel].length > 0) {
                hasValidParentContext = true;
                break;
              }
            }
          }
        }

        // Restore values with editing support
        // Restoration logic:
        // - If filter values exist (active editing session) → preserve them
        // - Otherwise, restore from drillDownPath (first open or after cleanup)
        const committedValues = getValuesFromPath(geoDrillDownPath, value);
        const currentValues = prev[value] || [];

        if (currentValues.length > 0) {
          // Active editing session - preserve current values
          newFilters[value] = [...currentValues];
        } else if (committedValues.length > 0) {
          // First open - restore from drillDownPath
          newFilters[value] = [...committedValues];
        } else if (!hasValidParentContext) {
          // Clear if no values and no valid parent context
          if (newFilters[value]) {
            delete newFilters[value];
          }
        }
        // If no values but valid parent, leave empty (ready for new selection)
      } else {
        delete newFilters[levelKey];
      }

      return newFilters;
    });

    // Only load values if:
    // 1. Value is not empty
    // 2. Options don't exist in cache OR switching to a different level
    // Note: if switching levels but cache exists, loadGeographicalValues will use cache
    const hasCache = value
      ? geoOptionsCache[value] && geoOptionsCache[value].length > 0
      : false;
    const hasOptionsInMap = value
      ? optionsMap[value] && optionsMap[value].length > 0
      : false;
    const shouldLoad =
      value && (!hasOptionsInMap || (prevLevel !== value && !hasCache));

    if (shouldLoad) {
      loadGeographicalValues(value);
    } else if (value && hasCache && !hasOptionsInMap) {
      // Restore from cache without API call
      setOptionsMap((prev) => ({ ...prev, [value]: geoOptionsCache[value] }));
    }

    // Clear distributor search text when geography hierarchy level changes
    if (distributorFilter?.current?.enabled && distributorFilter?.current?.field) {
      setSearchTextMap((prev) => {
        const updated = { ...prev };
        delete updated[distributorFilter!.current?.field];
        return updated;
      });
    }
  };
    const handleConfirmSourceChange = useCallback(() => {
      if (!pendingSourceChange.current) return;
      
      const newSource = pendingSourceChange.current;
      
      // Clear the previous hierarchy selections
      if (newSource === "geographical") {
        clearSalesHierarchySelections();
      } else {
        clearGeoHierarchySelections();
      }
      
      // Switch to new source
      setDistributorSource(newSource);
      clearDistributorSelections();
      // Clear distributor search text when switching source
      if (distributorFilter?.current?.field) {
        setSearchTextMap(prev => {
          const updated = { ...prev };
          delete updated[distributorFilter!.current?.field];
          return updated;
        });
      }
      
      // Reset pending change
      pendingSourceChange.current = null;
    }, [clearSalesHierarchySelections, clearGeoHierarchySelections, clearDistributorSelections, distributorFilter?.current?.field]);
  
    const handleCancelSourceChange = useCallback(() => {
      pendingSourceChange.current = null;
      setShowSourceChangeConfirm(false);
    }, []);

  const handleGeographicalValueChange = (values: string[]) => {
    const levelKey = "geographicalLevel";
    const currentLevel = filters[levelKey]?.[0];

    if (!currentLevel) return;

    // When parent selections change, clear cache for all child levels
    const currentLevelIndex = geoHierarchyOrder.indexOf(currentLevel);

    if (currentLevelIndex !== -1) {
      setOptionsMap((prev) => {
        const updated = { ...prev };
        // Clear cache for all levels after this one
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete updated[geoHierarchyOrder[i]];
        }
        // Also clear distributor cache
        if (distributorFilter?.current?.enabled) {
          delete updated[distributorFilter.current?.field];
        }

        return updated;
      });

      setGeoOptionsCache((prev) => {
        const updated = { ...prev };
        for (let i = currentLevelIndex + 1; i < geoHierarchyOrder.length; i++) {
          delete updated[geoHierarchyOrder[i]];
        }
        return updated;
      });

      setSearchTextMap((prev) => {
        const updated = { ...prev };

        return updated;
      });
    }

    // Update filter values - DON'T clear child values (preserve during editing)
    setFilters((prev) => ({
      ...prev,
      [currentLevel]: values,
    }));

    // DON'T update drillDownPath here - only update on Apply button!
  };
  const handleMultiFilterChange = (key: string, values: string[]) => {
    const shouldClearDistributor = 
      (key === 'distributor_type' || key === 'distributor_division') && 
      values.length === 0 &&
    distributorFilter?.current?.enabled;
    
    const distributorFilterField = distributorFilter?.current?.field;
    const hierarchyOrder = salesHierarchyFilter?.current?.hierarchyOrder || [];
    const supplierLevel = hierarchyOrder.length > 0 ? hierarchyOrder[hierarchyOrder.length - 1] : 'supplier';
    
    const isDistributorChange = 
      key === distributorFilterField || 
      key === supplierLevel ||
      key === 'distributor_type' ||
      key === 'distributor_division';
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: [...values]
      };
      
      // Clear distributor selection in the same update if type or division filter is cleared
      if (shouldClearDistributor && distributorFilterField) {
        if (newFilters[distributorFilterField]) {
          delete newFilters[distributorFilterField];
        }
      }
      
      // Clear custom filter selections when distributor type/division changes
     
      
      return newFilters;
    });
    
    if (isDistributorChange) {
      setOptionsMap(prev => {
        const updated = { ...prev };
       
        return updated;
      });
      
      setSearchTextMap(prev => {
        const updated = { ...prev };
       
        return updated;
      });
    }
    
    if (shouldClearDistributor && distributorFilterField) {
      setOptionsMap(prev => {
        if (!prev[distributorFilterField]) {
          return prev;
        }
        const updated = { ...prev };
        delete updated[distributorFilterField];
        return updated;
      });
      setSearchTextMap(prev => {
        if (!prev[distributorFilterField]) {
          return prev;
        }
        const updated = { ...prev };
        delete updated[distributorFilterField];
        return updated;
      });
    }
  };
   const salesPathSummary = useMemo(
      () => summarizeDrillDownPath(salesDrillDownPath),
      [salesDrillDownPath]
    );
  
    const geoPathSummary = useMemo(
      () => summarizeDrillDownPath(geoDrillDownPath),
      [geoDrillDownPath]
    );

    const getFilterChips = () => {
      const chips: Array<{ 
        key: string; 
        label: string; 
        value: string; 
        count?: number;
        type: 'filter' | 'geo' | 'sales' | 'distributor';
        level?: string;
        startIndex?: number;
      }> = [];
  
      // Distributor Type
      if (filters['distributor_type']?.length > 0) {
        const values = filters['distributor_type'];
        chips.push({
          key: 'distributor_type',
          label: 'Distributor type',
          value: values[0],
          count: values.length > 1 ? values.length - 1 : undefined,
          type: 'filter'
        });
      }
  
      // Distributor Division
      if (filters['distributor_division']?.length > 0) {
        const values = filters['distributor_division'];
        chips.push({
          key: 'distributor_division',
          label: 'Distributor division',
          value: values[0],
          count: values.length > 1 ? values.length - 1 : undefined,
          type: 'filter'
        });
      }
  
      // Distributor selection
      if (distributorFilter?.current?.enabled && distributorFieldKey) {
        const distributorValues = filters[distributorFieldKey] || [];
        if (distributorValues.length > 0) {
          // Get labels for selected distributors from optionsMap
          const distributorOptions = optionsMap[distributorFieldKey] || [];
          const firstValueLabel = distributorOptions.find(opt => opt.value === distributorValues[0])?.label || distributorValues[0];
          
          chips.push({
            key: distributorFieldKey,
            label: 'Distributor',
            value: firstValueLabel,
            count: distributorValues.length > 1 ? distributorValues.length - 1 : undefined,
            type: 'distributor'
          });
        }
      }
  
      const pushHierarchyChip = (
        summaryItem: PathSummaryItem,
        type: 'geo' | 'sales'
      ) => {
        const extraCount = summaryItem.count > 1 ? summaryItem.count - 1 : 0;
        chips.push({
          key: `${type}_${summaryItem.level}_${summaryItem.startIndex}`,
          label: summaryItem.level,
          value: summaryItem.firstValue || "",
          count: extraCount > 0 ? extraCount : undefined,
          type,
          level: summaryItem.level,
          startIndex: summaryItem.startIndex
        });
      };
  
      geoPathSummary.forEach(item => pushHierarchyChip(item, 'geo'));
      salesPathSummary.forEach(item => pushHierarchyChip(item, 'sales'));
  
      return chips;
    };
    const handleGeoPathReset = (level: string, startIndex: number) => {
    if (!geographicalHierarchyFilter?.current) return;
    
    const levelKey = geographicalHierarchyFilter?.current?.levelFilterField;
    
    // Get levels that will be removed from the path
    const levelsToClear = new Set<string>();
    levelsToClear.add(level);
    
    // Trim path and collect levels to clear
    setGeoDrillDownPath(prev => {
      // Get all levels from the path that come after the reset point
      const levelsAfterReset = prev.slice(startIndex).map(item => item.level);
      levelsAfterReset.forEach(l => levelsToClear.add(l));
      const newPath = prev.slice(0, startIndex);
      return newPath;
    });
    
    // Also clear ALL child levels in hierarchy order (not just those in path)
    // This handles cases where child values were uncommitted but still in filters
    const levelIndex = geoHierarchyOrder.indexOf(level);
    if (levelIndex !== -1) {
      for (let i = levelIndex; i < geoHierarchyOrder.length; i++) {
        levelsToClear.add(geoHierarchyOrder[i]);
      }
    }
    
    setFilters(prev => {
      const newFilters = { ...prev };
      
      // Clear the level dropdown selection
      delete newFilters[levelKey];
      
      // Clear all levels that are being reset
      levelsToClear.forEach(levelToClear => {
        delete newFilters[levelToClear];
      });
      
    
      
      return newFilters;
    });
    
    setOptionsMap(prev => {
      const updated = { ...prev };
      
      // Clear options cache for all levels being reset
      levelsToClear.forEach(levelToClear => {
        delete updated[levelToClear];
      });
      
      
      
      return updated;
    });

    setGeoOptionsCache(prev => {
      const updated = { ...prev };
      
      levelsToClear.forEach(levelToClear => {
        delete updated[levelToClear];
      });
      
      return updated;
    });

    if (distributorSource === "geographical" && distributorFilter?.current?.enabled) {
      clearDistributorSelections();
    } else if (distributorFilter?.current?.enabled && distributorFilter?.current?.field) {
      // Clear distributor search text when geography hierarchy values change (even if not the active source)
      setSearchTextMap(prev => {
        const updated = { ...prev };
        delete updated[distributorFilter?.current?.field];
        return updated;
      });
    }

    loadGeographicalLevels();
  };
  
    // Handle chip deletion
    const handleChipDelete = (chip: any) => {
      if (chip.type === 'filter') {
        const shouldClearDistributor = 
          (chip.key === 'distributor_type' || chip.key === 'distributor_division') && 
          distributorFilter?.current?.enabled;
        
        // Simple filter deletion
        setFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters[chip.key];
          
          // Clear distributor selection in the same update if type or division filter chip is deleted
          if (shouldClearDistributor && distributorFilter?.current?.field) {
            const distributorFieldKey = distributorFilter.current?.field;
            if (newFilters[distributorFieldKey]) {
              delete newFilters[distributorFieldKey];
            }
          }
          
          return newFilters;
        });
        
        // Also clear distributor from optionsMap and searchTextMap if needed
        if (shouldClearDistributor && distributorFilter?.current?.field) {
          const distributorFieldKey = distributorFilter?.current?.field;
          setOptionsMap(prev => {
            if (!prev[distributorFieldKey]) {
              return prev;
            }
            const updated = { ...prev };
            delete updated[distributorFieldKey];
            return updated;
          });
          setSearchTextMap(prev => {
            if (!prev[distributorFieldKey]) {
              return prev;
            }
            const updated = { ...prev };
            delete updated[distributorFieldKey];
            return updated;
          });
        }
      } else if (chip.type === 'distributor') {
        // Distributor deletion - clear distributor selections
        clearDistributorSelections();
      } else if (chip.type === 'geo') {
        // Geographical hierarchy deletion - remove this and all children
        handleGeoPathReset(chip.level!, chip.startIndex!);
      } else if (chip.type === 'sales') {
        // Sales hierarchy deletion - remove this and all children
        handleSalesPathReset(chip.level!, chip.startIndex!);
      }
    };
  
    const filterChips = getFilterChips();
  

    const isGeoLastLevel = useMemo(() => {
      if (!geographicalHierarchyFilter?.current?.enabled || geoHierarchyOrder.length === 0) {
        return false;
      }
      const levelKey = geographicalHierarchyFilter?.current?.levelFilterField;
      const currentLevel = filters[levelKey]?.[0];
      if (!currentLevel) return false;
  
      const lastLevel = geoHierarchyOrder[geoHierarchyOrder.length - 1];
      return currentLevel === lastLevel;
    }, [geographicalHierarchyFilter, geoHierarchyOrder, filters]);
  const handleGeoDropdownClose = useCallback(() => {
      if (!geographicalHierarchyFilter) return;
      
      // If Apply was just clicked, skip cleanup and just reset the flag
      if (geoApplyJustClicked.current) {
        geoApplyJustClicked.current = false;
        setIsHierarchySyncing(false);
        return;
      }
      
      const levelKey = geographicalHierarchyFilter?.current?.levelFilterField;
      
      setFilters(prev => {
        const newFilters = { ...prev };
        let hasChanges = false;
        
        // For each level in hierarchy, sync with drillDownPath
        geoHierarchyOrder.forEach(level => {
          const filterValues = prev[level] || [];
          const committedValues = getValuesFromPath(geoDrillDownPath, level);
          
          // Always sync to committed values (either clear or update)
          if (JSON.stringify(filterValues.sort()) !== JSON.stringify(committedValues.sort())) {
            if (committedValues.length > 0) {
              newFilters[level] = [...committedValues];
            } else {
              delete newFilters[level];
            }
            hasChanges = true;
          }
        });
        
        // Clear level selection if no committed path exists
        if (geoDrillDownPath.length === 0 && newFilters[levelKey]) {
          delete newFilters[levelKey];
          hasChanges = true;
        }
        
        return hasChanges ? newFilters : prev;
      });
      
      // Clear options cache for uncommitted levels
      setOptionsMap(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        geoHierarchyOrder.forEach(level => {
          const committedValues = getValuesFromPath(geoDrillDownPath, level);
          if (committedValues.length === 0 && updated[level]) {
            delete updated[level];
            hasChanges = true;
          }
        });
        
        return hasChanges ? updated : prev;
      });
      
      setGeoOptionsCache(prev => {
        const updated = { ...prev };
        let hasChanges = true;
        
        geoHierarchyOrder.forEach(level => {
          const committedValues = getValuesFromPath(geoDrillDownPath, level);
          if (committedValues.length === 0 && updated[level]) {
            delete updated[level];
            hasChanges = true;
          }
        });
        
        return hasChanges ? updated : prev;
      });
      
      // Reset syncing state after cleanup completes
      setIsHierarchySyncing(false);
    }, [geographicalHierarchyFilter, geoDrillDownPath, geoHierarchyOrder]);
  
   const handleGeoResetAll = () => {
    if (!geographicalHierarchyFilter) return;
    
    const levelKey = geographicalHierarchyFilter?.current?.levelFilterField;
    setFilters(prev => {
      const newFilters = { ...prev };
      // Clear level selection
      delete newFilters[levelKey];
      // Clear all level values from geo hierarchy order
      geoHierarchyOrder.forEach(level => {
        delete newFilters[level];
      });
      return newFilters;
    });
    
    // Clear drill-down path
    setGeoDrillDownPath([]);
    
    // Clear options cache
    setOptionsMap(prev => {
      const updated = { ...prev };
      geoHierarchyOrder.forEach(level => {
        delete updated[level];
      });
      return updated;
    });
    setGeoOptionsCache({});

    // Clear distributor selection if distributor source is geographical
    if (distributorSource === "geographical" &&distributorFilter?.current?.enabled) {
      clearDistributorSelections();
    }
  };
    const handleFilterChange = (key: string, value: string | null) => {
    setFilters(prev => ({
      ...prev,
      [key]: value ? [value] : []
    }));
  };
  const loadDistributorOptions = useCallback(
    async (fieldKey: string) => {
      setLoadingMap((prev) => ({ ...prev, [fieldKey]: true }));
      try {
        let filterIds: string[] = [];
        let attemptedTargetedFetch = false;

        // Get filter IDs from hierarchy selections
        if (
          distributorSource === "geographical" &&
          geographicalHierarchyFilter?.current?.enabled
        ) {
          if (geoDrillDownPath.length > 0) {
            const level = geoDrillDownPath[geoDrillDownPath.length - 1].level;
            const values = geoDrillDownPath
              .filter((item) => item.level === level)
              .map((item) => item.value);
            if (values.length > 0) {
              attemptedTargetedFetch = true;
              const supplierPromises = values.map((value) =>
                fetchLocationUsers(level, value, "supplier")
              );
              const supplierResults = await Promise.all(supplierPromises);
              filterIds = Array.from(new Set(supplierResults.flat()));
            }
          }
        }

        if (distributorSource === "sales" && salesHierarchyFilter?.current?.enabled) {
          const hierarchyOrder = salesHierarchyFilter.current.hierarchyOrder || [];
          if (hierarchyOrder.length > 0 && salesDrillDownPath.length > 0) {
            const supplierLevel = hierarchyOrder[hierarchyOrder.length - 1];

            const supplierSelections = salesDrillDownPath
              .filter((item) => item.level === supplierLevel)
              .map((item) => item.value);

            if (supplierSelections.length > 0) {
              attemptedTargetedFetch = true;
              filterIds = supplierSelections;
            } else {
              const targetParent = [...salesDrillDownPath]
                .reverse()
                .find((item) => {
                  const levelIndex = hierarchyOrder.indexOf(item.level);
                  const supplierIndex = hierarchyOrder.indexOf(supplierLevel);
                  return (
                    levelIndex !== -1 &&
                    supplierIndex !== -1 &&
                    levelIndex < supplierIndex
                  );
                });

              if (targetParent) {
                attemptedTargetedFetch = true;
                const parentSelections = salesDrillDownPath
                  .filter((item) => item.level === targetParent.level)
                  .map((item) => item.value);

                const childrenPromises = parentSelections.map((userId) =>
                  fetchChildrenUsers(userId, supplierLevel)
                );
                const childrenResults = await Promise.all(childrenPromises);
                filterIds = Array.from(new Set(childrenResults.flat()));
              }
            }
          }
        }

        // If no hierarchy selection, get all suppliers
        if (filterIds.length === 0 && !attemptedTargetedFetch) {
          const allSuppliers = await fetchUsersByDesignation("supplier");
          filterIds = allSuppliers;
        }

        // Get selected type and division from filters
        const selectedTypes = filters["distributor_type"] || [];
        const selectedDivisions = filters["distributor_division"] || [];

        // Filter distributors based on type/division selections and filter IDs
        let filteredLoginIds: string[] = [];

        // If type or division is selected, use distributorFeatures for matching
        // Otherwise, use the filterIds directly (from hierarchy or fetchUsersByDesignation)
        if (
          (selectedTypes.length > 0 || selectedDivisions.length > 0) &&
          distributorFeatures.length > 0
        ) {
          // Use the stored features to filter based on type/division
          // Then intersect with filterIds if they exist
          filteredLoginIds = filterDistributorsBySelections(
            distributorFeatures,
            selectedTypes,
            selectedDivisions,
            filterIds.length > 0 ? filterIds : undefined
          );
        } else {
          // No type/division selection, use filterIds directly (from hierarchy or fetchUsersByDesignation)
          filteredLoginIds = filterIds;
        }

        const opts = filteredLoginIds.map((u) => ({ label: u, value: u }));
        setOptionsMap((prev) => ({ ...prev, [fieldKey]: opts }));
      } catch (error) {
        setOptionsMap((prev) => ({ ...prev, [fieldKey]: [] }));
      } finally {
        setLoadingMap((prev) => ({ ...prev, [fieldKey]: false }));
      }
    },
    [
      filters,
      geoDrillDownPath,
      salesDrillDownPath,
      distributorSource,
      distributorFeatures,
    ]
  );
  useEffect(()=>{
    props.setValues({
      ...props.values,
      ["distributor"]: {
        ...props.values?.["distributor"],
        value:filters?.["distributor_code"]??[] ,
      },
    });
  },[filters?.["distributor_code"]])

  return (
    <div style={{ width: "100%", padding: "2px", display: "flex", flexDirection: "column" }} >
<ConfirmationPopUp
        openConfirmModal={showSourceChangeConfirm}
        message="Do you want to clear the selection and continue?"
        setOpenConfirmModal={(value) => {
 
          setShowSourceChangeConfirm(value);
          if (!value) {
            // Reset pending change if dialog is closed (e.g., clicking outside or ESC)
            pendingSourceChange.current = null;
          }
        }}
        successMethod={handleConfirmSourceChange}
        rejectMethod={handleCancelSourceChange}
        rightButtonText="Continue"
      />
    <Box sx={{ flex: 1, gap:'10px' }} >
      <DistributorTopFilter
      distributorFilter={distributorFilter?.current}
      geographicalHierarchyFilter={geographicalHierarchyFilter?.current}
      salesHierarchyFilter={salesHierarchyFilter?.current}
        filters={filters}
        optionsMap={optionsMap}
        loadingMap={loadingMap}
        onFilterChange={handleFilterChange}
        onMultiFilterChange={handleMultiFilterChange}
        onSalesLevelChange={handleSalesLevelChange}
        onSalesValueChange={handleSalesValueChange}
        onSalesLoadLevels={loadSalesLevels}
        onSalesNextLevel={handleSalesNextLevel}
        onSalesResetAll={handleSalesResetAll}
        onSalesDropdownClose={handleSalesDropdownClose}
        onGeoLevelChange={handleGeographicalLevelChange}
        onGeoValueChange={handleGeographicalValueChange}
        onGeoLoadLevels={loadGeographicalLevels}
        onGeoNextLevel={handleGeoNextLevel}
        onGeoResetAll={handleGeoResetAll}
        onGeoDropdownClose={handleGeoDropdownClose}
        geoIsLastLevel={isGeoLastLevel}
        salesDrillDownPath={salesDrillDownPath}
        geoDrillDownPath={geoDrillDownPath}
        distributorSource={distributorSource}
        onDistributorSourceChange={handleDistributorSourceChange}
        onLoadDistributorOptions={loadDistributorOptions}
        searchTextMap={searchTextMap}
        onFilterInputChange={handleFilterInputChange}
        isHierarchySyncing={isHierarchySyncing}
      />
    </Box>
      {filterChips.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 2,
                mb: 2,
                px: 2
              }}
            >
              {filterChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={
                    <span>
                      {chip.label}: <strong>{chip.value || "--"}</strong>
                      {chip.count !== undefined && chip.count > 0 && ` +${chip.count}`}
                    </span>
                  }
                  onDelete={() => handleChipDelete(chip)}
                  sx={{
                    backgroundColor: "#E6FBF8",
                    color: "#169C87",
                    fontWeight: 500,
                    fontSize: "13px",
                    height: "28px",
                    "& .MuiChip-label": {
                      px: 1.5
                    },
                    "& .MuiChip-deleteIcon": {
                      color: "#169C87",
                      fontSize: "18px",
                      "&:hover": {
                        color: "#0D7A6A"
                      }
                    }
                  }}
                />
              ))}
            </Box>
          )}
    </div>
  );
};

export default FullHerirarchy;
